// frontend/src/context/ThemeContext.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-06-04
// Updated:      2026-06-22
//
// Description:
//   React context provider for applying dynamic tenant theme settings from backend.
//
// AUD8-FE-STATE:
//   - Uses sessionStorage, not localStorage, for last authenticated tenant theme.
//   - Caches only authenticated tenant themes.
//   - Strips oversized base64 logos before caching to avoid mobile/session bloat.
//   - Login page can use last tenant colours/fonts quickly without storing huge assets long-term.

import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

export const ThemeContext = createContext(null);

const LAST_THEME_KEY = "anpr_session_last_authenticated_theme";
const MAX_CACHE_BYTES = 1500000;

const fallback = {
  client_name: "Vision",
  primary_color: "#1a1a1a",
  secondary_color: "#e63946",
  accent_color: "#ff6600",
  primary_hover: "#333333",
  secondary_hover: "#ff7a7a",
  accent_hover: "#ffa366",
  font_header: "Arial, sans-serif",
  font_subheader: "Verdana, sans-serif",
  font_body: "Tahoma, sans-serif",
  dark_mode: false,
  logo_login_url: null,
  logo_header_url: null,
  button_general: { normal: "#1a1a1a", hover: "#333333", click: "#000000" },
  button_action: { normal: "#e63946", hover: "#ff4d4d", click: "#cc0000" },
};

const API_BASE = (process.env.REACT_APP_API_BASE || "/api").replace(/\/+$/, "");

const buildApiUrl = (path) => {
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return API_BASE.endsWith("/api") ? `${API_BASE}${cleanPath}` : `${API_BASE}/api${cleanPath}`;
};

const safeValue = (value, fallbackValue) => {
  if (value === null || value === undefined || value === "") return fallbackValue;
  return value;
};

const mergeTheme = (data) => ({
  ...fallback,
  ...(data || {}),
  button_general: {
    ...fallback.button_general,
    ...(data?.button_general || {}),
  },
  button_action: {
    ...fallback.button_action,
    ...(data?.button_action || {}),
  },
});

const hasToken = () =>
  Boolean(
    localStorage.getItem("anpr_token") ||
      sessionStorage.getItem("anpr_token") ||
      localStorage.getItem("token")
  );

const loadCachedTheme = () => {
  try {
    const raw = sessionStorage.getItem(LAST_THEME_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const cacheTheme = (theme) => {
  try {
    if (!theme?.tenant_id) return;

    const full = mergeTheme(theme);
    const fullJson = JSON.stringify(full);

    if (fullJson.length <= MAX_CACHE_BYTES) {
      sessionStorage.setItem(LAST_THEME_KEY, fullJson);
      return;
    }

    const reduced = {
      ...full,
      logo_login_url: null,
      logo_header_url: null,
    };

    sessionStorage.setItem(LAST_THEME_KEY, JSON.stringify(reduced));
  } catch {
    // ignore storage failures
  }
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    if (!hasToken()) return mergeTheme(loadCachedTheme() || fallback);
    return fallback;
  });

  const applyCSSVariables = useCallback((config) => {
    const root = document.documentElement;
    const applied = mergeTheme(config);

    root.style.setProperty("--primary-color", safeValue(applied.primary_color, fallback.primary_color));
    root.style.setProperty("--secondary-color", safeValue(applied.secondary_color, fallback.secondary_color));
    root.style.setProperty("--accent-color", safeValue(applied.accent_color, fallback.accent_color));
    root.style.setProperty("--background-mode", applied.dark_mode ? "#111" : "#fff");

    root.style.setProperty("--primary-hover", safeValue(applied.primary_hover, fallback.primary_hover));
    root.style.setProperty("--secondary-hover", safeValue(applied.secondary_hover, fallback.secondary_hover));
    root.style.setProperty("--accent-hover", safeValue(applied.accent_hover, fallback.accent_hover));

    root.style.setProperty("--font-header", safeValue(applied.font_header, fallback.font_header));
    root.style.setProperty("--font-subheader", safeValue(applied.font_subheader, fallback.font_subheader));
    root.style.setProperty("--font-body", safeValue(applied.font_body, fallback.font_body));

    root.style.setProperty("--btn-general-normal", safeValue(applied.button_general?.normal, fallback.button_general.normal));
    root.style.setProperty("--btn-general-hover", safeValue(applied.button_general?.hover, fallback.button_general.hover));
    root.style.setProperty("--btn-general-click", safeValue(applied.button_general?.click, fallback.button_general.click));

    root.style.setProperty("--btn-action-normal", safeValue(applied.button_action?.normal, fallback.button_action.normal));
    root.style.setProperty("--btn-action-hover", safeValue(applied.button_action?.hover, fallback.button_action.hover));
    root.style.setProperty("--btn-action-click", safeValue(applied.button_action?.click, fallback.button_action.click));

    root.style.setProperty("--button-general-normal", safeValue(applied.button_general?.normal, fallback.button_general.normal));
    root.style.setProperty("--button-general-hover", safeValue(applied.button_general?.hover, fallback.button_general.hover));
    root.style.setProperty("--button-general-click", safeValue(applied.button_general?.click, fallback.button_general.click));

    root.style.setProperty("--button-action-normal", safeValue(applied.button_action?.normal, fallback.button_action.normal));
    root.style.setProperty("--button-action-hover", safeValue(applied.button_action?.hover, fallback.button_action.hover));
    root.style.setProperty("--button-action-click", safeValue(applied.button_action?.click, fallback.button_action.click));
  }, []);

  const injectGoogleFont = useCallback((fontName) => {
    if (!fontName) return;

    const cleaned = String(fontName).replace(/['"]/g, "").split(",")[0].trim();
    if (!cleaned) return;

    const fontUrl = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(cleaned).replace(/%20/g, "+")}&display=swap`;
    if (document.querySelector(`link[href="${fontUrl}"]`)) return;

    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = fontUrl;
    document.head.appendChild(link);
  }, []);

  const applyTheme = useCallback(
    (data, shouldCache = false) => {
      const applied = mergeTheme(data);

      injectGoogleFont(applied.font_header);
      injectGoogleFont(applied.font_subheader);
      injectGoogleFont(applied.font_body);

      applyCSSVariables(applied);
      setTheme(applied);

      if (shouldCache) cacheTheme(applied);
    },
    [applyCSSVariables, injectGoogleFont]
  );

  const refreshTheme = useCallback(async () => {
    const token =
      localStorage.getItem("anpr_token") ||
      sessionStorage.getItem("anpr_token") ||
      localStorage.getItem("token") ||
      "";

    const cachedTheme = loadCachedTheme();

    if (!token && cachedTheme) {
      applyTheme(cachedTheme, false);
      return;
    }

    try {
      const res = await fetch(buildApiUrl("/ui-config"), {
        method: "GET",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!res.ok) throw new Error(`Theme request failed with HTTP ${res.status}`);

      const data = await res.json();
      applyTheme(data, Boolean(token && data?.tenant_id));
    } catch (err) {
      console.warn("Failed to refresh theme:", err?.message || err);

      if (!token && cachedTheme) {
        applyTheme(cachedTheme, false);
      } else {
        applyTheme(fallback, false);
      }
    }
  }, [applyTheme]);

  useEffect(() => {
    refreshTheme();
  }, [refreshTheme]);

  return (
    <ThemeContext.Provider value={{ theme, refreshTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);